import { parseParams, quote } from './param-values.js';
import { isIterable } from './utils.js';
/**
 * The value of a `Cookie` HTTP header.
 *
 * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cookie)
 */
export class Cookie {
    #map;
    constructor(init) {
        this.#map = new Map();
        if (init) {
            if (typeof init === 'string') {
                let params = parseParams(init);
                for (let [name, value] of params) {
                    this.#map.set(name, value || '');
                }
            }
            else if (isIterable(init)) {
                for (let [name, value] of init) {
                    this.#map.set(name, value);
                }
            }
            else {
                for (let name in init) {
                    if (Object.prototype.hasOwnProperty.call(init, name)) {
                        this.#map.set(name, init[name]);
                    }
                }
            }
        }
    }
    /**
     * Gets the value of a cookie with the given name from the `Cookie` header.
     */
    get(name) {
        return this.#map.get(name);
    }
    /**
     * Sets a cookie with the given name and value in the `Cookie` header.
     */
    set(name, value) {
        this.#map.set(name, value);
    }
    /**
     * Removes a cookie with the given name from the `Cookie` header.
     */
    delete(name) {
        return this.#map.delete(name);
    }
    /**
     * True if a cookie with the given name exists in the `Cookie` header.
     */
    has(name) {
        return this.#map.has(name);
    }
    /**
     * Removes all cookies from the `Cookie` header.
     */
    clear() {
        this.#map.clear();
    }
    entries() {
        return this.#map.entries();
    }
    names() {
        return this.#map.keys();
    }
    values() {
        return this.#map.values();
    }
    [Symbol.iterator]() {
        return this.entries();
    }
    forEach(callback, thisArg) {
        this.#map.forEach(callback, thisArg);
    }
    /**
     * The number of cookies in the `Cookie` header.
     */
    get size() {
        return this.#map.size;
    }
    toString() {
        let pairs = [];
        for (let [name, value] of this.#map) {
            pairs.push(`${name}=${quote(value)}`);
        }
        return pairs.join('; ');
    }
}
